import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated, { interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import StatusBar from 'design-system/StatusBar';
import { rem, Selector, AppView, NavigationHeader } from 'design-system';
import useFetch, { muteAll } from 'hooks/useFetch';
import useStyles from 'hooks/useStyles';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { FinancialsPeriod } from '../types';
import CashFlow from '../components/CashFlow';
import BalanceSheet from '../components/BalanceSheet';
import IncomeStatement from '../components/IncomeStatement';
import { getStockFinancials } from '../actions';
import { isWeb } from '../../../constants';
const SECTIONS = Object.values(FinancialsPeriod);
const FinancialScreen = ({ route, navigation }) => {
    const assetId = route.params?.assetId;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrollY = useSharedValue(0);
    const colorScheme = useColorScheme();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Financials',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const financialsDataForAssetId = useAppSelector((state) => state.stockInfoReducer.financials?.[assetId]);
    const [, , , fetchFinancials] = useFetch(muteAll);
    const onScroll = useAnimatedScrollHandler(({ contentOffset: { y } }) => {
        scrollY.value = y;
    });
    const [selection, setSelection] = useState(SECTIONS[0]);
    const handleSelection = useCallback((index) => {
        const value = SECTIONS[index];
        setSelection(value);
        switch (value) {
            case FinancialsPeriod.ANNUAL:
                if (!financialsDataForAssetId?.annual) {
                    fetchFinancials(getStockFinancials(assetId));
                }
                break;
            case FinancialsPeriod.QUARTERLY:
                if (!financialsDataForAssetId?.quarterly) {
                    fetchFinancials(getStockFinancials(assetId, FinancialsPeriod.QUARTERLY));
                }
                break;
            default:
                break;
        }
    }, [fetchFinancials, financialsDataForAssetId?.annual, financialsDataForAssetId?.quarterly, assetId]);
    const animatedBg = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], colorScheme === 'dark'
            ? [colors.cards.onDark, colors.background.dark]
            : [colors.cards.onDark, colors.cards.onLight]),
    }), [scrollY, colorScheme, colors.cards.onDark, colors.cards.onLight, colors.background.dark]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} withFrame>
        <Selector Text-14 sections={SECTIONS} onSelected={handleSelection} style={[styles.selector, animatedBg]}/>
      </NavigationHeader>
      <Animated.ScrollView scrollEventThrottle={8} onScroll={onScroll} contentContainerStyle={contentContainerStyle}>
        <BalanceSheet assetId={assetId} selection={selection}/>
        <CashFlow assetId={assetId} selection={selection}/>
        <IncomeStatement assetId={assetId} selection={selection}/>
      </Animated.ScrollView>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    selector: {
        marginHorizontal: rem(16),
        marginTop: rem(8),
        marginBottom: rem(16),
        width: null,
    },
}));
export default FinancialScreen;
